import { showSuccessNotification } from "@/composables/useNotifications";
import { i18nGlobal as i18n } from "@/setup/i18n";

export const camelToSnakeCase = (str) =>
  str.replace(/[A-Z0-9]/g, (ltr, idx) =>
    idx > 0 ? `_${ltr.toLowerCase()}` : ltr.toLowerCase()
  );

export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

// tri naturel : « phase 2 » avant « phase 10 »
export const naturalCompare = (a, b) =>
  String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true });

export const sortByLabel = (items) =>
  items.toSorted((a, b) => naturalCompare(a.label, b.label));

const compareValues = (a, b) => {
  if (a instanceof Date || typeof a === "number") return a - b;
  return naturalCompare(a, b);
};

export const sortByOrdering = (items, ordering) => {
  const fields = ordering.split(",").filter(Boolean);
  return items.toSorted((a, b) => {
    for (const field of fields) {
      const key = field.replace("-", "");
      const [left, right] = [a[key], b[key]];
      if (left == null && right == null) continue;
      if (left == null) return 1;
      if (right == null) return -1;
      const result = compareValues(left, right);
      if (result) return field.startsWith("-") ? -result : result;
    }
    return 0;
  });
};

export const truncate = (text, stop, endChar) => {
  if (!text) {
    return "";
  }
  return text.slice(0, stop) + (stop < text.length ? endChar || "..." : "");
};

export function toCamelCase(inputString) {
  return inputString
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/[^a-zA-Z0-9]+/g, "");
}

export const chainFilters =
  (...filterFunctions) =>
  (item) => {
    return filterFunctions
      .map((filterFn) => filterFn(item))
      .every((filterFnResult) => filterFnResult === true);
  };

export const containsIgnoreCase = (value, query) =>
  typeof value === "string" &&
  value.toLowerCase().includes(String(query).toLowerCase());

export const icontains = (item, keyVal) => {
  const [key, value] = Object.entries(keyVal)[0];

  if (value === null || !value) return true;

  if (Array.isArray(item[key]) && Array.isArray(value)) {
    return value.some((el) => item[key].includes(el));
  }

  return item[key].toLowerCase().includes(value?.toLowerCase());
};

export const exact = (item, keyVal) => {
  const [key, value] = Object.entries(keyVal)[0];
  if (value === null) return true;
  if (item[key] != value?.toLowerCase()) {
    return false;
  }
  return true;
};

export const groupBy = (list, keyGetter) => {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
};

export const getArraySum = (array) => {
  return array.reduce((pre, curr) => +pre + +curr, 0);
};

export const setReadableNumber = (value, locale = "fr") => {
  // Value must be a number
  // Add spaces each 3 digits (or comma, depending on the region)
  return value.toLocaleString(locale);
};

export const copyContentsToClipboard = (valueToCopy) => {
  navigator.clipboard.writeText(valueToCopy);
  showSuccessNotification(i18n.t("copiedToClipboard"));
};

export const formatFilters = (filters) => {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [
        camelToSnakeCase(key),
        value,
      ])
    )
  );
};

export const readIntFormat = (value) => {
  const cleanValue = value.toString().replace(",", ".").replace(/\s/g, "");

  if (Number.isNaN(Number(cleanValue)) || !value) return "";

  return new Intl.NumberFormat(i18n.locale.value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.parseFloat(cleanValue));
};

export const writeIntFormat = (item, property, value) => {
  const inputFormatted = Number.parseFloat(
    value.replace(",", ".").replace(/\s/g, "")
  ).toFixed(2);
  if (Number.isNaN(Number(inputFormatted))) {
    item[property] = "";
  } else {
    item[property] = inputFormatted;
  }
};

export const readFloatFormat = (value, toFixed) => {
  return Number.parseFloat(value).toFixed(toFixed);
};
export const writeFloatFormat = (value, toFixed) => {
  if (value.includes(",")) {
    value.replace(",", ".");
  }
  return Number.parseFloat(value).toFixed(toFixed);
};

export const getUuidSplit = (str) =>
  str && str.includes("-") ? str.split("-")[0] : str;

/**
Checks if a string starts with any of the given substrings (case-insensitive).
@param {string} str - The string to check.
@param {string[]} substrs - An array of substrings to check against.
@returns {boolean} - True if the string starts with any of the given substrings, false otherwise.

*/

export function hasPrefix(str, substrs) {
  try {
    str = str.toLowerCase();
    substrs = substrs.map((substr) => substr.toLowerCase());
    return substrs.some((substr) => str.startsWith(substr));
  } catch (err) {
    console.error("Something went wrong", err);
  }
}

export const arrayNonPresentKeys = (data, keysArray) => {
  // For a data Object (dict), returns the keys that are not in the keysArray array
  return Object.keys(data).filter((k) => !keysArray.includes(k));
};

const PARAM_SUFFIX_RE = /^(?:part|gaz)\.?$/i;
export const normalizeGazPartParameterName = (name) => {
  const trimmed = name.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  return lastSpace !== -1 && PARAM_SUFFIX_RE.test(trimmed.slice(lastSpace + 1))
    ? trimmed.slice(0, lastSpace).trimEnd()
    : trimmed;
};

export const getFieldValueFromFieldsMap = (fieldsMap, field, valueType) => {
  // Returns the value of a field in a fieldsMap Object, given the field and the valueType
  const [, { [valueType]: value }] = fieldsMap.find(
    ([fieldName]) => fieldName === field
  );
  return value;
};

export const formatDecimal = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const num = Number.parseFloat(value);

  if (Number.isNaN(num)) {
    return "";
  }

  return String(Number(num));
};
